/**
 * Property Card Wizard — non-GIS guided flow with advanced options + viral share.
 * Satellite → search → tap corners → labels → DIGIPIN/address → QR + multi-channel share.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
  Alert,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import UndoIcon from '@mui/icons-material/Undo';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import SatelliteAltIcon from '@mui/icons-material/SatelliteAlt';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import BadgeIcon from '@mui/icons-material/Badge';
import TuneIcon from '@mui/icons-material/Tune';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ShareIcon from '@mui/icons-material/Share';
import TelegramIcon from '@mui/icons-material/Telegram';
import EmailIcon from '@mui/icons-material/Email';
import XIcon from '@mui/icons-material/X';
import LinkIcon from '@mui/icons-material/Link';
import MapIcon from '@mui/icons-material/Map';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import OSM from 'ol/source/OSM';
import XYZ from 'ol/source/XYZ';
import { fromLonLat, toLonLat } from 'ol/proj';
import { Feature } from 'ol';
import { Point, LineString, Polygon } from 'ol/geom';
import { Style, Fill, Stroke, Circle as CircleStyle, Text } from 'ol/style';
import Overlay from 'ol/Overlay';
import 'ol/ol.css';
import jsPDF from 'jspdf';
import { geocodingService, type GeocodingResult } from '../../services/GeocodingService';
import { encodeDigipin } from '../../utils/digipinEncoder';
import {
  computeParcelMetrics,
  formatCoord,
  formatLength,
  buildViralShareText,
  type LonLat,
  type ParcelMetrics,
  type LengthUnit,
  type AreaUnit,
} from '../../utils/propertyCardGeometry';
import length from '@turf/length';
import { lineString } from '@turf/helpers';
import { trackEvent } from '../../services/analyticsService';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { addNotification } from '../../store/slices/uiSlice';
import { addLayer, setActiveLayer } from '../../store/slices/layersSlice';
import { publishShareLink } from '../../services/shareMapService';
import { canSaveToCloud } from '../../services/cloudProjectService';
import { useNavigate } from 'react-router-dom';

const STEPS = ['Find place', 'Tap corners', 'Card & share'];

const SAT_URL =
  'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
const HYBRID_LABELS =
  'https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}';

type BaseMode = 'satellite' | 'hybrid' | 'streets';

interface PropertyCardWizardProps {
  open: boolean;
  onClose: () => void;
}

export default function PropertyCardWizard({ open, onClose }: PropertyCardWizardProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);

  const mapHostRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<Map | null>(null);
  const vectorSrcRef = useRef<VectorSource | null>(null);
  const baseGroupRef = useRef<TileLayer[]>([]);
  const labelsRef = useRef<Overlay[]>([]);
  const stepRef = useRef(0);
  const closedRef = useRef(false);

  const [step, setStep] = useState(0);
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<GeocodingResult[]>([]);
  const [corners, setCorners] = useState<LonLat[]>([]);
  const [closed, setClosed] = useState(false);
  const [parcelName, setParcelName] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [notes, setNotes] = useState('');
  const [address, setAddress] = useState<string | null>(null);
  const [metrics, setMetrics] = useState<ParcelMetrics | null>(null);
  const [digipin, setDigipin] = useState<string | null>(null);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [publicMapUrl, setPublicMapUrl] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [hint, setHint] = useState(
    'Search your village or use GPS — free digital property card on satellite.'
  );
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [baseMode, setBaseMode] = useState<BaseMode>('satellite');
  const [lengthUnit, setLengthUnit] = useState<LengthUnit>('m');
  const [areaUnit, setAreaUnit] = useState<AreaUnit>('auto');
  const [showSideLabels, setShowSideLabels] = useState(true);
  const [showCenterLabel, setShowCenterLabel] = useState(true);
  const [fillColor, setFillColor] = useState('rgba(132, 204, 22, 0.28)');
  const [strokeColor, setStrokeColor] = useState('#84cc16');

  useEffect(() => {
    stepRef.current = step;
  }, [step]);
  useEffect(() => {
    closedRef.current = closed;
  }, [closed]);

  const clearLabelOverlays = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    labelsRef.current.forEach((ov) => map.removeOverlay(ov));
    labelsRef.current = [];
  }, []);

  const addHtmlLabel = useCallback((lonLat: LonLat, html: string, kind: 'side' | 'center') => {
    const map = mapRef.current;
    if (!map) return;
    const el = document.createElement('div');
    el.innerHTML = html;
    el.style.cssText =
      kind === 'side'
        ? 'background:rgba(9,21,18,0.92);color:#c7ec9b;font:700 11px Inter,sans-serif;padding:2px 6px;border-radius:6px;border:1px solid rgba(132,204,22,0.45);white-space:nowrap;pointer-events:none;box-shadow:0 2px 8px rgba(0,0,0,0.4);'
        : 'background:rgba(15,25,18,0.95);color:#f1f5e9;font:800 12px Inter,sans-serif;padding:6px 10px;border-radius:10px;border:1px solid rgba(56,189,248,0.5);text-align:center;pointer-events:none;box-shadow:0 4px 16px rgba(0,0,0,0.45);max-width:220px;';
    const ov = new Overlay({ element: el, positioning: 'center-center', stopEvent: false });
    ov.setPosition(fromLonLat(lonLat));
    map.addOverlay(ov);
    labelsRef.current.push(ov);
  }, []);

  const applyBasemap = useCallback((mode: BaseMode) => {
    const map = mapRef.current;
    if (!map) return;
    baseGroupRef.current.forEach((l) => map.removeLayer(l));
    baseGroupRef.current = [];
    if (mode === 'streets') {
      const l = new TileLayer({ source: new OSM(), zIndex: 0 });
      map.getLayers().insertAt(0, l);
      baseGroupRef.current = [l];
    } else {
      const sat = new TileLayer({
        source: new XYZ({ url: SAT_URL, maxZoom: 19, attributions: '© Esri' }),
        zIndex: 0,
      });
      map.getLayers().insertAt(0, sat);
      baseGroupRef.current = [sat];
      if (mode === 'hybrid') {
        const labels = new TileLayer({
          source: new XYZ({ url: HYBRID_LABELS, maxZoom: 19 }),
          zIndex: 1,
          opacity: 0.95,
        });
        map.getLayers().insertAt(1, labels);
        baseGroupRef.current.push(labels);
      }
    }
  }, []);

  const redrawMapFeatures = useCallback(
    (
      pts: LonLat[],
      isClosed: boolean,
      m: ParcelMetrics | null,
      name: string,
      pin: string | null
    ) => {
      const src = vectorSrcRef.current;
      if (!src) return;
      src.clear();
      clearLabelOverlays();

      pts.forEach((p, i) => {
        const f = new Feature({ geometry: new Point(fromLonLat(p)) });
        f.setStyle(
          new Style({
            image: new CircleStyle({
              radius: 7,
              fill: new Fill({ color: strokeColor }),
              stroke: new Stroke({ color: '#0d190e', width: 2 }),
            }),
            text: new Text({
              text: String(i + 1),
              font: 'bold 11px sans-serif',
              fill: new Fill({ color: '#0d190e' }),
            }),
          })
        );
        src.addFeature(f);
      });

      if (pts.length >= 2 && !isClosed) {
        const line = new Feature({
          geometry: new LineString(pts.map((p) => fromLonLat(p))),
        });
        line.setStyle(
          new Style({
            stroke: new Stroke({ color: strokeColor, width: 2.5, lineDash: [8, 6] }),
          })
        );
        src.addFeature(line);
        // Provisional edge labels while drawing
        if (showSideLabels) {
          for (let i = 0; i < pts.length - 1; i++) {
            const a = pts[i];
            const b = pts[i + 1];
            const metres = length(lineString([a, b]), { units: 'meters' });
            const mid: LonLat = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
            addHtmlLabel(mid, `S${i + 1}: ${formatLength(metres, lengthUnit)}`, 'side');
          }
        }
      }

      if (isClosed && m) {
        const poly = new Feature({
          geometry: new Polygon([m.ring.map((p) => fromLonLat(p))]),
        });
        poly.setStyle(
          new Style({
            fill: new Fill({ color: fillColor }),
            stroke: new Stroke({ color: strokeColor, width: 3 }),
          })
        );
        src.addFeature(poly);

        if (showSideLabels) {
          m.sides.forEach((s) => addHtmlLabel(s.mid, s.label, 'side'));
        }
        if (showCenterLabel) {
          const centerHtml = [
            name ? `<div style="color:#38bdf8;margin-bottom:2px">${escapeHtml(name)}</div>` : '',
            `<div style="color:#84cc16">${escapeHtml(m.areaLabel)}</div>`,
            pin
              ? `<div style="color:#93c5fd;font-size:11px;margin-top:2px">DIGIPIN ${escapeHtml(pin)}</div>`
              : '',
          ].join('');
          addHtmlLabel(m.centroid, centerHtml, 'center');
        }
      }
    },
    [
      addHtmlLabel,
      clearLabelOverlays,
      fillColor,
      strokeColor,
      showSideLabels,
      showCenterLabel,
      lengthUnit,
      areaUnit,
    ]
  );

  // Init map
  useEffect(() => {
    if (!open) return;
    setStep(0);
    setCorners([]);
    setClosed(false);
    setMetrics(null);
    setDigipin(null);
    setAddress(null);
    setShareUrl(null);
    setPublicMapUrl(null);
    setParcelName('');
    setOwnerName('');
    setNotes('');
    setResults([]);
    setQuery('');
    setHint('Search your village or use GPS — free digital property card on satellite.');
    trackEvent('property_card_open', {});

    let map: Map | null = null;
    const t = window.setTimeout(() => {
      if (!mapHostRef.current) return;
      const vectorSource = new VectorSource();
      vectorSrcRef.current = vectorSource;
      const vectorLayer = new VectorLayer({ source: vectorSource, zIndex: 20 });
      const sat = new TileLayer({
        source: new XYZ({ url: SAT_URL, maxZoom: 19 }),
        zIndex: 0,
      });
      baseGroupRef.current = [sat];

      map = new Map({
        target: mapHostRef.current,
        layers: [sat, vectorLayer],
        view: new View({
          center: fromLonLat([77.5946, 12.9716]),
          zoom: 5,
          maxZoom: 19,
        }),
        controls: [],
      });
      mapRef.current = map;
      map.on('singleclick', (evt) => {
        window.dispatchEvent(
          new CustomEvent('pc:mapclick', { detail: { coordinate: evt.coordinate } })
        );
      });
      map.updateSize();
    }, 80);

    return () => {
      window.clearTimeout(t);
      clearLabelOverlays();
      map?.setTarget(undefined);
      mapRef.current = null;
      vectorSrcRef.current = null;
      baseGroupRef.current = [];
    };
  }, [open, clearLabelOverlays]);

  useEffect(() => {
    if (open && mapRef.current) applyBasemap(baseMode);
  }, [baseMode, open, applyBasemap]);

  useEffect(() => {
    const onClick = (e: Event) => {
      if (stepRef.current !== 1 || closedRef.current) return;
      const coord = (e as CustomEvent).detail?.coordinate as number[] | undefined;
      if (!coord) return;
      const [lon, lat] = toLonLat(coord);
      setCorners((prev) => {
        const next: LonLat[] = [...prev, [lon, lat]];
        setHint(
          next.length < 3
            ? `Corner ${next.length} · tap ${3 - next.length} more (min 3).`
            : `Corner ${next.length} · keep tapping or Close shape.`
        );
        return next;
      });
    };
    window.addEventListener('pc:mapclick', onClick);
    return () => window.removeEventListener('pc:mapclick', onClick);
  }, []);

  // Recompute metrics when units change and shape closed
  useEffect(() => {
    if (!closed || corners.length < 3) {
      redrawMapFeatures(corners, false, null, parcelName, digipin);
      return;
    }
    const m = computeParcelMetrics(corners, { lengthUnit, areaUnit });
    setMetrics(m);
    redrawMapFeatures(corners, true, m, parcelName, digipin);
  }, [
    corners,
    closed,
    lengthUnit,
    areaUnit,
    parcelName,
    digipin,
    fillColor,
    strokeColor,
    showSideLabels,
    showCenterLabel,
    redrawMapFeatures,
  ]);

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => mapRef.current?.updateSize(), 120);
    return () => window.clearTimeout(t);
  }, [open, step, showAdvanced]);

  const handleSearch = async () => {
    if (!query.trim()) return;
    setSearching(true);
    setResults([]);
    try {
      const hits = await geocodingService.geocodeAddress(query.trim());
      setResults(hits);
      if (!hits.length) setHint('No places found — try a nearby town or PIN code.');
      trackEvent('property_card_search', { hits: hits.length });
    } finally {
      setSearching(false);
    }
  };

  const goToResult = (r: GeocodingResult) => {
    mapRef.current?.getView().animate({
      center: fromLonLat(r.coordinates),
      zoom: 18,
      duration: 600,
    });
    setAddress(r.address);
    setResults([]);
    setHint('Zoom to your plot, then Next → tap corners on satellite.');
    trackEvent('property_card_locate', { placeType: r.placeType });
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      dispatch(addNotification({ type: 'error', message: 'Location not available' }));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lon = pos.coords.longitude;
        const lat = pos.coords.latitude;
        mapRef.current?.getView().animate({
          center: fromLonLat([lon, lat]),
          zoom: 18,
          duration: 600,
        });
        setHint('You are here. Next: tap the corners of your land.');
        void geocodingService.reverseGeocode([lon, lat]).then((r) => {
          if (r) setAddress(r.address);
        });
      },
      () => dispatch(addNotification({ type: 'error', message: 'Could not get GPS location' })),
      { enableHighAccuracy: true, timeout: 12000 }
    );
  };

  const undoCorner = () => {
    if (closed) return;
    setCorners((prev) => prev.slice(0, -1));
  };

  const clearCorners = () => {
    setCorners([]);
    setClosed(false);
    setMetrics(null);
    setDigipin(null);
    setHint('Corners cleared — tap again to outline your plot.');
  };

  const closeShape = async () => {
    if (corners.length < 3) {
      dispatch(addNotification({ type: 'warning', message: 'Tap at least 3 corners first' }));
      return;
    }
    const m = computeParcelMetrics(corners, { lengthUnit, areaUnit });
    if (!m) {
      dispatch(addNotification({ type: 'error', message: 'Could not close that shape' }));
      return;
    }
    setMetrics(m);
    setClosed(true);
    const [lon, lat] = m.centroid;
    const pin = encodeDigipin(lat, lon);
    setDigipin(pin);
    setHint('Shape closed — sides & area labeled. Name it, then Create card.');
    trackEvent('property_card_closed', { sides: m.sides.length, areaM2: Math.round(m.areaM2) });
    if (!address) {
      const r = await geocodingService.reverseGeocode([lon, lat]);
      if (r) setAddress(r.address);
    }
    const map = mapRef.current;
    if (map) {
      const poly = new Polygon([m.ring.map((p) => fromLonLat(p))]);
      map.getView().fit(poly.getExtent(), {
        padding: [80, 40, 220, 40],
        maxZoom: 19,
        duration: 400,
      });
    }
  };

  const fitParcel = () => {
    if (!metrics) return;
    const map = mapRef.current;
    if (!map) return;
    const poly = new Polygon([metrics.ring.map((p) => fromLonLat(p))]);
    map.getView().fit(poly.getExtent(), { padding: [80, 40, 200, 40], maxZoom: 19, duration: 350 });
  };

  const buildLayerPayload = (name: string, localId: string, shareUrlForProps?: string | null) => {
    if (!metrics) return null;
    const sideProps: Record<string, string | number> = {};
    metrics.sides.forEach((s) => {
      sideProps[`Side_${s.index}_m`] = Number(s.metres.toFixed(2));
      sideProps[`Side_${s.index}_Label`] = s.label;
    });
    const props: Record<string, unknown> = {
      name,
      Owner: ownerName || '',
      Notes: notes || '',
      Parcel_ID: localId.slice(-8).toUpperCase(),
      DIGIPIN: digipin || '',
      Area_Acres: Number(metrics.areaAcres.toFixed(3)),
      Area_SqFt: Math.round(metrics.areaSqFt),
      Area_m2: Math.round(metrics.areaM2),
      Area_Label: metrics.areaLabel,
      Perimeter_m: Number(metrics.perimeterM.toFixed(2)),
      Perimeter_Label: metrics.perimeterLabel,
      Address: address || '',
      Side_Count: metrics.sides.length,
      Share_URL: shareUrlForProps || '',
      QR_URL: shareUrlForProps || '',
      ...sideProps,
    };
    return {
      id: localId,
      name,
      type: 'vector' as const,
      geometryType: 'Polygon' as const,
      visible: true,
      opacity: 1,
      zIndex: 20,
      featureCount: 1,
      crs: 'EPSG:4326',
      attributeFields: [
        { name: 'name', type: 'string' },
        { name: 'Owner', type: 'string' },
        { name: 'DIGIPIN', type: 'string' },
        { name: 'Area_Label', type: 'string' },
        { name: 'Perimeter_Label', type: 'string' },
        { name: 'Address', type: 'string' },
        { name: 'Share_URL', type: 'string' },
        { name: 'QR_URL', type: 'string' },
        ...metrics.sides.map((s) => ({ name: `Side_${s.index}_Label`, type: 'string' as const })),
      ],
      symbology: {
        type: 'simple' as const,
        fillColor,
        strokeColor,
        strokeWidth: 3,
        opacity: 0.85,
      },
      features: [
        {
          id: `${localId}-f0`,
          layerId: localId,
          geometry: {
            type: 'Polygon' as const,
            coordinates: [metrics.ring],
          },
          properties: props,
        },
      ],
    };
  };

  const finishCard = async () => {
    if (!metrics || !closed) return;
    const name = parcelName.trim() || 'My property';
    const localId = `parcel-${Date.now().toString(36)}`;

    // Soft viral URL first (updated if public publish works)
    const params = new URLSearchParams();
    params.set('from', 'property-card');
    params.set('viral', '1');
    if (digipin) params.set('digipin', digipin);
    params.set('name', name);
    let finalShare = `${window.location.origin}/map?${params.toString()}`;

    let layer = buildLayerPayload(name, localId, finalShare);
    if (!layer) return;

    if (isAuthenticated && canSaveToCloud()) {
      setPublishing(true);
      try {
        const pub = await publishShareLink([layer], { preferredLayerId: layer.id });
        if (pub.success && pub.shareUrl) {
          finalShare = pub.shareUrl;
          setPublicMapUrl(pub.shareUrl);
          layer = buildLayerPayload(name, localId, finalShare) || layer;
          trackEvent('property_card_public_share', { layerId: pub.layerId });
        }
      } catch {
        /* keep soft url */
      } finally {
        setPublishing(false);
      }
    }

    dispatch(addLayer(layer));
    dispatch(setActiveLayer(localId));
    setShareUrl(finalShare);

    setStep(2);
    setHint('Card ready — share QR, WhatsApp, or copy link. Free forever on GeoVera.');
    trackEvent('property_card_ready', {
      hasDigipin: !!digipin,
      sides: metrics.sides.length,
      viral: true,
    });
    dispatch(
      addNotification({
        type: 'success',
        message: 'Property card ready — share it to invite others to GeoVera free.',
      })
    );
  };

  const viralText = useMemo(() => {
    if (!shareUrl) return '';
    return buildViralShareText({
      name: parcelName.trim() || 'My property',
      digipin,
      areaLabel: metrics?.areaLabel,
      address,
      perimeterLabel: metrics?.perimeterLabel,
      shareUrl,
      owner: ownerName || undefined,
    });
  }, [shareUrl, parcelName, digipin, metrics, address, ownerName]);

  const qrImgUrl = useMemo(() => {
    if (!shareUrl) return null;
    return `https://api.qrserver.com/v1/create-qr-code/?size=220x220&margin=10&data=${encodeURIComponent(shareUrl)}`;
  }, [shareUrl]);

  const copyShare = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(viralText || shareUrl);
      dispatch(addNotification({ type: 'success', message: 'Share text + link copied' }));
      trackEvent('property_card_copy', {});
    } catch {
      dispatch(addNotification({ type: 'error', message: 'Could not copy' }));
    }
  };

  const openShare = (channel: 'wa' | 'tg' | 'x' | 'mail') => {
    if (!shareUrl) return;
    const text = viralText;
    const enc = encodeURIComponent(text);
    const urlEnc = encodeURIComponent(shareUrl);
    if (channel === 'wa') {
      window.open(`https://api.whatsapp.com/send?text=${enc}`, '_blank', 'noopener');
    } else if (channel === 'tg') {
      window.open(`https://t.me/share/url?url=${urlEnc}&text=${enc}`, '_blank', 'noopener');
    } else if (channel === 'x') {
      window.open(`https://twitter.com/intent/tweet?text=${enc}`, '_blank', 'noopener');
    } else {
      window.location.href = `mailto:?subject=${encodeURIComponent('GeoVera Property Card')}&body=${enc}`;
    }
    trackEvent('property_card_share', { channel });
  };

  const exportPdf = () => {
    if (!metrics) return;
    const name = parcelName.trim() || 'My property';
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    doc.setFillColor(11, 19, 43);
    doc.rect(0, 0, 210, 40, 'F');
    doc.setTextColor(132, 204, 22);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('GEOVERA PROPERTY CARD', 14, 18);
    doc.setTextColor(148, 163, 184);
    doc.setFontSize(10);
    doc.text('Free digital land card · satellite outline · DIGIPIN', 14, 28);

    doc.setTextColor(15, 23, 42);
    doc.setFontSize(16);
    doc.text(name, 14, 55);
    if (ownerName) {
      doc.setFontSize(11);
      doc.setTextColor(71, 85, 105);
      doc.text(`Owner: ${ownerName}`, 14, 63);
    }
    doc.setFontSize(12);
    doc.setTextColor(30, 64, 175);
    if (digipin) doc.text(`DIGIPIN: ${digipin}`, 14, 72);
    doc.setTextColor(22, 101, 52);
    doc.text(`Area: ${metrics.areaLabel}`, 14, 80);
    doc.setTextColor(71, 85, 105);
    doc.text(metrics.perimeterLabel, 14, 88);
    if (address) {
      const lines = doc.splitTextToSize(`Address: ${address}`, 180);
      doc.text(lines, 14, 98);
    }
    let y = 115;
    doc.setFontSize(11);
    doc.setTextColor(15, 23, 42);
    doc.text('Side lengths', 14, y);
    y += 7;
    metrics.sides.forEach((s) => {
      doc.setTextColor(51, 65, 85);
      doc.text(s.label, 18, y);
      y += 6;
    });
    y += 6;
    doc.setTextColor(100, 116, 139);
    doc.setFontSize(9);
    doc.text(formatCoord(metrics.centroid[0], metrics.centroid[1]), 14, y);
    y += 10;
    if (shareUrl) {
      doc.setTextColor(37, 99, 235);
      doc.textWithLink('Open on GeoVera (free)', 14, y, { url: shareUrl });
    }
    y += 14;
    doc.setTextColor(148, 163, 184);
    doc.setFontSize(8);
    doc.text('Made free on GeoVera WebGIS · geovera.in · SIFA Geolabs', 14, y);
    doc.save(`${name.replace(/\s+/g, '_')}_GeoVera_PropertyCard.pdf`);
    trackEvent('property_card_pdf', {});
    dispatch(addNotification({ type: 'success', message: 'PDF property card downloaded' }));
  };

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          bgcolor: '#0B132B',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        },
      }}
    >
      {/* Single unified top bar: brand + search + steps + actions */}
      <Box
        sx={{
          px: { xs: 1, sm: 1.5 },
          py: 1,
          bgcolor: 'rgba(15,23,42,0.98)',
          borderBottom: '1px solid rgba(219,234,254,0.12)',
          zIndex: 30,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={{ xs: 0.75, sm: 1.25 }}
          sx={{ width: '100%', minWidth: 0 }}
        >
          {/* Brand */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.75}
            sx={{ flexShrink: 0, display: { xs: 'none', md: 'flex' } }}
          >
            <BadgeIcon sx={{ color: '#38bdf8', fontSize: 22 }} />
            <Typography
              sx={{ color: '#F8FAFC', fontWeight: 800, fontSize: 15, whiteSpace: 'nowrap' }}
            >
              Property card
            </Typography>
          </Stack>

          {/* Search — always in the same bar */}
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            sx={{ flex: 1, minWidth: 0, maxWidth: { sm: 420, md: 520 } }}
          >
            <TextField
              size="small"
              fullWidth
              placeholder="Search village, city, PIN…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && void handleSearch()}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: '#64748B', fontSize: 18, mr: 0.75 }} />,
                sx: {
                  color: '#F8FAFC',
                  bgcolor: 'rgba(0,0,0,0.28)',
                  borderRadius: 2,
                  fontSize: 13,
                  '& fieldset': { borderColor: 'rgba(56,189,248,0.25)' },
                },
              }}
            />
            <Tooltip title="Search">
              <IconButton
                onClick={() => void handleSearch()}
                disabled={searching}
                size="small"
                sx={{
                  bgcolor: '#38bdf8',
                  color: '#0B132B',
                  borderRadius: 2,
                  width: 36,
                  height: 36,
                  flexShrink: 0,
                  '&:hover': { bgcolor: '#7dd3fc' },
                }}
              >
                {searching ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SearchIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
            <Tooltip title="Use my location">
              <IconButton
                onClick={useMyLocation}
                size="small"
                sx={{
                  color: '#7dd3fc',
                  border: '1px solid rgba(56,189,248,0.35)',
                  borderRadius: 2,
                  width: 36,
                  height: 36,
                  flexShrink: 0,
                }}
              >
                <MyLocationIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>

          {/* Compact step pills */}
          <Stack
            direction="row"
            spacing={0.5}
            sx={{ display: { xs: 'none', sm: 'flex' }, flexShrink: 0 }}
          >
            {STEPS.map((label, i) => (
              <Chip
                key={label}
                size="small"
                label={`${i + 1}. ${label}`}
                onClick={() => {
                  if (i < step || (i === 1 && step === 0) || (i === 2 && closed)) {
                    /* allow going back only, or forward if ready */
                  }
                  if (i <= step) setStep(i);
                  if (i === 1 && step === 0) {
                    setStep(1);
                    setHint('Tap each corner of your land (min 3).');
                  }
                }}
                sx={{
                  height: 26,
                  fontWeight: 700,
                  fontSize: 11,
                  bgcolor: i === step ? 'rgba(132,204,22,0.22)' : 'rgba(255,255,255,0.05)',
                  color: i === step ? '#c7ec9b' : i < step ? '#94A3B8' : '#64748B',
                  border: i === step ? '1px solid rgba(132,204,22,0.45)' : '1px solid transparent',
                }}
              />
            ))}
          </Stack>

          {/* Step tools when drawing */}
          {step === 1 && (
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
              <Chip
                size="small"
                icon={<TouchAppIcon sx={{ fontSize: '14px !important' }} />}
                label={corners.length}
                sx={{
                  bgcolor: 'rgba(132,204,22,0.15)',
                  color: '#c7ec9b',
                  fontWeight: 800,
                  height: 26,
                }}
              />
              {!closed ? (
                <>
                  <Tooltip title="Undo corner">
                    <span>
                      <IconButton
                        size="small"
                        disabled={!corners.length}
                        onClick={undoCorner}
                        sx={{ color: '#94A3B8' }}
                      >
                        <UndoIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Clear">
                    <span>
                      <IconButton
                        size="small"
                        disabled={!corners.length}
                        onClick={clearCorners}
                        sx={{ color: '#F87171' }}
                      >
                        <DeleteOutlinedIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={corners.length < 3}
                    onClick={() => void closeShape()}
                    startIcon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 800,
                      bgcolor: '#84cc16',
                      color: '#0d190e',
                      height: 32,
                      display: { xs: 'none', sm: 'inline-flex' },
                    }}
                  >
                    Close shape
                  </Button>
                </>
              ) : (
                <Tooltip title="Fit to parcel">
                  <IconButton size="small" onClick={fitParcel} sx={{ color: '#84cc16' }}>
                    <CenterFocusStrongIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          )}

          <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0, ml: 'auto' }}>
            <Tooltip title="Advanced options">
              <IconButton
                onClick={() => setShowAdvanced((v) => !v)}
                size="small"
                sx={{ color: showAdvanced ? '#38bdf8' : '#94A3B8' }}
              >
                <TuneIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton onClick={onClose} size="small" sx={{ color: '#94A3B8' }} aria-label="Close">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>

        {/* Hint + mobile step row */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          sx={{ mt: 0.75, minWidth: 0 }}
        >
          <Typography
            sx={{
              color: '#94A3B8',
              fontSize: 12,
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {hint}
          </Typography>
          <Stack
            direction="row"
            spacing={0.5}
            sx={{ display: { xs: 'flex', sm: 'none' }, flexShrink: 0 }}
          >
            {STEPS.map((_, i) => (
              <Box
                key={i}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: i === step ? '#84cc16' : i < step ? '#64748B' : 'rgba(255,255,255,0.15)',
                }}
              />
            ))}
          </Stack>
          {step === 1 && !closed && corners.length >= 3 && (
            <Button
              size="small"
              variant="contained"
              onClick={() => void closeShape()}
              sx={{
                display: { xs: 'inline-flex', sm: 'none' },
                textTransform: 'none',
                fontWeight: 800,
                bgcolor: '#84cc16',
                color: '#0d190e',
                flexShrink: 0,
              }}
            >
              Close
            </Button>
          )}
        </Stack>

        {/* Search results dropdown under unified bar */}
        {results.length > 0 && (
          <Paper
            elevation={0}
            sx={{
              mt: 0.75,
              bgcolor: 'rgba(0,0,0,0.35)',
              border: '1px solid rgba(56,189,248,0.25)',
              borderRadius: 2,
              maxHeight: 200,
              overflow: 'auto',
            }}
          >
            <List dense disablePadding>
              {results.map((r) => (
                <ListItemButton
                  key={r.placeId || r.address}
                  onClick={() => goToResult(r)}
                  sx={{ borderRadius: 1, color: '#E2E8F0' }}
                >
                  <ListItemText
                    primary={r.address}
                    primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }}
                    secondary={r.placeType}
                    secondaryTypographyProps={{ fontSize: 11, color: '#64748B' }}
                  />
                </ListItemButton>
              ))}
            </List>
          </Paper>
        )}
      </Box>

      {/* Advanced options panel */}
      <Collapse in={showAdvanced}>
        <Paper
          square
          elevation={0}
          sx={{
            px: 2,
            py: 1.5,
            bgcolor: 'rgba(15,23,42,0.98)',
            borderBottom: '1px solid rgba(56,189,248,0.2)',
          }}
        >
          <Typography sx={{ color: '#7dd3fc', fontWeight: 800, fontSize: 12, mb: 1.25 }}>
            ADVANCED OPTIONS
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' },
              gap: 1.5,
            }}
          >
            <FormControl size="small" fullWidth>
              <InputLabel sx={{ color: '#94A3B8' }}>Basemap</InputLabel>
              <Select
                label="Basemap"
                value={baseMode}
                onChange={(e) => setBaseMode(e.target.value as BaseMode)}
                sx={{
                  color: '#F8FAFC',
                  '.MuiOutlinedInput-notchedOutline': { borderColor: '#334155' },
                }}
              >
                <MenuItem value="satellite">Satellite HD</MenuItem>
                <MenuItem value="hybrid">Hybrid (labels)</MenuItem>
                <MenuItem value="streets">Streets</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel sx={{ color: '#94A3B8' }}>Length</InputLabel>
              <Select
                label="Length"
                value={lengthUnit}
                onChange={(e) => setLengthUnit(e.target.value as LengthUnit)}
                sx={{
                  color: '#F8FAFC',
                  '.MuiOutlinedInput-notchedOutline': { borderColor: '#334155' },
                }}
              >
                <MenuItem value="m">Metres</MenuItem>
                <MenuItem value="ft">Feet</MenuItem>
                <MenuItem value="yd">Yards</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel sx={{ color: '#94A3B8' }}>Area</InputLabel>
              <Select
                label="Area"
                value={areaUnit}
                onChange={(e) => setAreaUnit(e.target.value as AreaUnit)}
                sx={{
                  color: '#F8FAFC',
                  '.MuiOutlinedInput-notchedOutline': { borderColor: '#334155' },
                }}
              >
                <MenuItem value="auto">Auto (ac + sq ft)</MenuItem>
                <MenuItem value="acre">Acres</MenuItem>
                <MenuItem value="sqft">Sq feet</MenuItem>
                <MenuItem value="m2">Square metres</MenuItem>
                <MenuItem value="hectare">Hectares</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Fill colour"
              value={fillColor}
              onChange={(e) => setFillColor(e.target.value)}
              InputLabelProps={{ sx: { color: '#94A3B8' } }}
              InputProps={{ sx: { color: '#F8FAFC' } }}
            />
            <TextField
              size="small"
              label="Outline colour"
              value={strokeColor}
              onChange={(e) => setStrokeColor(e.target.value)}
              InputLabelProps={{ sx: { color: '#94A3B8' } }}
              InputProps={{ sx: { color: '#F8FAFC' } }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={showSideLabels}
                  onChange={(e) => setShowSideLabels(e.target.checked)}
                  color="success"
                />
              }
              label={
                <Typography sx={{ color: '#CBD5E1', fontSize: 13 }}>Side length labels</Typography>
              }
            />
            <FormControlLabel
              control={
                <Switch
                  checked={showCenterLabel}
                  onChange={(e) => setShowCenterLabel(e.target.checked)}
                  color="success"
                />
              }
              label={
                <Typography sx={{ color: '#CBD5E1', fontSize: 13 }}>Center name / area</Typography>
              }
            />
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1.5 }}>
            <TextField
              size="small"
              fullWidth
              label="Owner / holder (optional)"
              value={ownerName}
              onChange={(e) => setOwnerName(e.target.value)}
              InputLabelProps={{ sx: { color: '#94A3B8' } }}
              InputProps={{ sx: { color: '#F8FAFC' } }}
            />
            <TextField
              size="small"
              fullWidth
              label="Notes (optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              InputLabelProps={{ sx: { color: '#94A3B8' } }}
              InputProps={{ sx: { color: '#F8FAFC' } }}
            />
          </Stack>
        </Paper>
      </Collapse>

      {/* Map */}
      <Box sx={{ position: 'relative', flex: '1 1 auto', minHeight: { xs: 300, sm: 380 } }}>
        <Box
          ref={mapHostRef}
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: '#0a0f0c',
            cursor: step === 1 && !closed ? 'crosshair' : 'grab',
          }}
        />

        {step === 2 && metrics && (
          <Paper
            elevation={12}
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 30,
              maxHeight: '62%',
              overflow: 'auto',
              bgcolor: 'rgba(11,19,43,0.98)',
              borderTop: '1px solid rgba(132,204,22,0.35)',
              borderRadius: '16px 16px 0 0',
              p: 2,
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              sx={{ mb: 1 }}
            >
              <Box>
                <Typography sx={{ color: '#F8FAFC', fontWeight: 800, fontSize: 18 }}>
                  {parcelName.trim() || 'My property'}
                </Typography>
                {ownerName && (
                  <Typography sx={{ color: '#94A3B8', fontSize: 12 }}>
                    Owner: {ownerName}
                  </Typography>
                )}
              </Box>
              <Chip
                label="FREE on GeoVera"
                size="small"
                sx={{ bgcolor: 'rgba(132,204,22,0.2)', color: '#c7ec9b', fontWeight: 800 }}
              />
            </Stack>

            {digipin && (
              <Typography sx={{ color: '#38bdf8', fontWeight: 800, fontSize: 15, mb: 0.5 }}>
                DIGIPIN {digipin}
              </Typography>
            )}
            {address && (
              <Typography sx={{ color: '#94A3B8', fontSize: 12, mb: 1, lineHeight: 1.4 }}>
                {address}
              </Typography>
            )}
            <Typography sx={{ color: '#84cc16', fontWeight: 700, fontSize: 13 }}>
              {metrics.areaLabel}
            </Typography>
            <Typography sx={{ color: '#94A3B8', fontSize: 12, mb: 1 }}>
              {metrics.perimeterLabel} · {formatCoord(metrics.centroid[0], metrics.centroid[1])}
            </Typography>

            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
              {metrics.sides.map((s) => (
                <Chip
                  key={s.index}
                  size="small"
                  label={s.label}
                  sx={{ bgcolor: 'rgba(255,255,255,0.06)', color: '#cbd5e1', fontWeight: 600 }}
                />
              ))}
            </Stack>

            {publicMapUrl && (
              <Alert severity="success" sx={{ mb: 1.5, fontSize: 12 }}>
                Public map live: <strong>{publicMapUrl}</strong>
              </Alert>
            )}
            {publishing && (
              <Alert severity="info" sx={{ mb: 1.5, fontSize: 12 }}>
                Publishing public share link…
              </Alert>
            )}

            <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mb: 1.5 }} />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
              {qrImgUrl && (
                <Box
                  sx={{
                    p: 1.25,
                    bgcolor: '#fff',
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <Box
                    component="img"
                    src={qrImgUrl}
                    alt="QR to open GeoVera property"
                    sx={{ width: 150, height: 150 }}
                  />
                  <Typography sx={{ color: '#0B132B', fontSize: 10, fontWeight: 800 }}>
                    Scan → open free on GeoVera
                  </Typography>
                </Box>
              )}

              <Stack spacing={1} sx={{ flex: 1, width: '100%' }}>
                <Typography
                  sx={{ color: '#FBBF24', fontWeight: 800, fontSize: 12, letterSpacing: '0.06em' }}
                >
                  SHARE & GROW
                </Typography>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<WhatsAppIcon />}
                  onClick={() => openShare('wa')}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 800,
                    bgcolor: '#25D366',
                    color: '#042f1a',
                  }}
                >
                  WhatsApp
                </Button>
                <Stack direction="row" spacing={1}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<TelegramIcon />}
                    onClick={() => openShare('tg')}
                    sx={{ textTransform: 'none', borderColor: '#38bdf8', color: '#7dd3fc' }}
                  >
                    Telegram
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<XIcon />}
                    onClick={() => openShare('x')}
                    sx={{ textTransform: 'none', borderColor: '#64748B', color: '#E2E8F0' }}
                  >
                    X
                  </Button>
                </Stack>
                <Stack direction="row" spacing={1}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<EmailIcon />}
                    onClick={() => openShare('mail')}
                    sx={{ textTransform: 'none', borderColor: '#64748B', color: '#CBD5E1' }}
                  >
                    Email
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<ContentCopyIcon />}
                    onClick={() => void copyShare()}
                    sx={{
                      textTransform: 'none',
                      borderColor: 'rgba(132,204,22,0.5)',
                      color: '#84cc16',
                    }}
                  >
                    Copy
                  </Button>
                </Stack>
                <Stack direction="row" spacing={1}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<PictureAsPdfIcon />}
                    onClick={exportPdf}
                    sx={{ textTransform: 'none', borderColor: '#F87171', color: '#FCA5A5' }}
                  >
                    PDF card
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<ShareIcon />}
                    onClick={() => {
                      onClose();
                      window.dispatchEvent(new CustomEvent('geovera:open-share'));
                    }}
                    sx={{ textTransform: 'none', borderColor: '#a3e635', color: '#a3e635' }}
                  >
                    Public /m/ link
                  </Button>
                </Stack>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<MapIcon />}
                  onClick={() => {
                    onClose();
                    navigate('/map');
                  }}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 800,
                    bgcolor: '#84cc16',
                    color: '#0d190e',
                  }}
                >
                  Open free WebGIS map
                </Button>
                {!isAuthenticated && (
                  <Button
                    fullWidth
                    variant="text"
                    onClick={() => {
                      onClose();
                      navigate(
                        `/login?redirect=${encodeURIComponent('/map')}&message=${encodeURIComponent(
                          'Sign in free — save cards and publish public map links.'
                        )}`
                      );
                    }}
                    sx={{ textTransform: 'none', color: '#7dd3fc', fontWeight: 700 }}
                  >
                    Sign in free to save & publish forever
                  </Button>
                )}
              </Stack>
            </Stack>

            <Alert severity="info" sx={{ mt: 1.5, fontSize: 12 }}>
              Anyone can create a card free — share the QR so friends try GeoVera. No GIS skills
              needed.
            </Alert>
          </Paper>
        )}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          p: 1.5,
          borderTop: '1px solid rgba(219,234,254,0.1)',
          bgcolor: 'rgba(15,23,42,0.98)',
          display: 'flex',
          gap: 1,
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <Button
          onClick={() => {
            if (step === 0) onClose();
            else setStep((s) => Math.max(0, s - 1));
          }}
          sx={{ textTransform: 'none', color: '#94A3B8' }}
        >
          {step === 0 ? 'Cancel' : 'Back'}
        </Button>

        {step === 0 && (
          <Button
            variant="contained"
            onClick={() => {
              setStep(1);
              setHint('Tap each corner of your land on the satellite image (min 3).');
              mapRef.current?.updateSize();
            }}
            sx={{ textTransform: 'none', fontWeight: 800, bgcolor: '#84cc16', color: '#0d190e' }}
          >
            Next: tap corners
          </Button>
        )}

        {step === 1 && (
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            {closed && (
              <TextField
                size="small"
                placeholder="Name this property"
                value={parcelName}
                onChange={(e) => setParcelName(e.target.value)}
                sx={{
                  minWidth: { xs: 140, sm: 200 },
                  '& .MuiOutlinedInput-root': { color: '#F8FAFC', bgcolor: 'rgba(0,0,0,0.25)' },
                }}
              />
            )}
            <Button
              variant="contained"
              disabled={!closed || publishing}
              onClick={() => void finishCard()}
              startIcon={publishing ? <CircularProgress size={16} /> : <QrCode2Icon />}
              sx={{ textTransform: 'none', fontWeight: 800, bgcolor: '#38bdf8', color: '#0B132B' }}
            >
              Create card & share
            </Button>
          </Stack>
        )}

        {step === 2 && (
          <Button
            onClick={onClose}
            startIcon={<LinkIcon />}
            sx={{ textTransform: 'none', color: '#84cc16', fontWeight: 700 }}
          >
            Done
          </Button>
        )}
      </Box>
    </Dialog>
  );
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
